
// YouTube API-related interfaces
interface CaptionTrack {
  baseUrl: string;
  name: { simpleText: string };
  languageCode: string;
  isTranslatable: boolean;
}

interface VideoDetails {
  videoId: string;
  title: string;
  lengthSeconds: string;
  caption: boolean;
  captionTracks?: CaptionTrack[];
  description?: string;
  defaultLanguage?: string;
  defaultAudioLanguage?: string;
}

// YouTube Data API interfaces
interface YouTubeVideoResponse {
  items: Array<{
    id: string;
    snippet: {
      title: string;
      description: string;
      defaultLanguage?: string;
      defaultAudioLanguage?: string;
    };
    contentDetails: {
      duration: string;
    };
  }>;
}

// Supadata API interfaces
interface SupadataTranscriptResponse {
  content: string;
  lang: string;
  availableLangs: string[];
}

interface SupadataTranscriptJobResponse {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  content?: string;
  lang?: string;
  availableLangs?: string[];
  error?: string;
}

interface SupadataVideoMetadataResponse {
  id: string;
  title: string;
  description: string;
  duration: number;
  channel: {
    id: string;
    name: string;
  };
  tags: string[];
  thumbnail: string;
  uploadDate: string;
  viewCount: number;
  likeCount: number;
  transcriptLanguages: string[];
}

// Google Cloud API Key
const YOUTUBE_API_KEY = 'AIzaSyCBcgFvznJv9o71yW1vcF0ywwv_BDpicDI';

// Updated Supadata API configuration
const SUPADATA_API_KEY = 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiIsImtpZCI6IjEifQ.eyJpc3MiOiJuYWRsZXMiLCJpYXQiOiIxNzQ4NDY0Mzc3IiwicHVycG9zZSI6ImFwaV9hdXRoZW50aWNhdGlvbiIsInN1YiI6IjkzZDFlY2I4YzVjZTRiZWNiNTU0NTViY2ZhN2NmYTAyIn0.Eh0qr4NrtsbjkeFhV3BefsrzyFNS7vlUZuweHxfSkfE';
const SUPADATA_BASE_URL = 'https://api.supadata.ai/v1';

const normalizeLanguageCode = (language?: string): string | undefined => {
  if (!language) return undefined;
  const normalized = language.toLowerCase().replace(/_/g, '-').trim();
  if (!normalized) return undefined;
  return normalized.split('-')[0];
};

/**
 * Extract video ID from various social media platform URLs
 */
export const extractVideoId = (url: string): { videoId: string; platform: 'youtube' | 'tiktok' | 'instagram' } | null => {
  // YouTube patterns
  const youtubePatterns = [
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=|embed\/|v\/|shorts\/)?([^?&\s]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^?&\s]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([^?&\s]+)/
  ];

  // TikTok patterns
  const tiktokPatterns = [
    /(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@[^\/]+\/video\/(\d+)/,
    /(?:https?:\/\/)?(?:vm\.)?tiktok\.com\/([^?&\s]+)/,
    /(?:https?:\/\/)?(?:www\.)?tiktok\.com\/t\/([^?&\s]+)/
  ];

  // Instagram patterns
  const instagramPatterns = [
    /(?:https?:\/\/)?(?:www\.)?instagram\.com\/(?:p|reel)\/([^?&\s\/]+)/,
    /(?:https?:\/\/)?(?:www\.)?instagram\.com\/tv\/([^?&\s\/]+)/
  ];

  // Check YouTube
  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return { videoId: match[1], platform: 'youtube' };
    }
  }

  // Check TikTok
  for (const pattern of tiktokPatterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return { videoId: match[1], platform: 'tiktok' };
    }
  }

  // Check Instagram
  for (const pattern of instagramPatterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return { videoId: match[1], platform: 'instagram' };
    }
  }
  
  return null;
};

/**
 * Legacy function for backward compatibility
 */
export const extractYoutubeVideoId = (url: string): string | null => {
  const result = extractVideoId(url);
  return result?.platform === 'youtube' ? result.videoId : null;
};

/**
 * Get video metadata using Supadata API for different platforms
 */
const getSupadataVideoMetadata = async (videoId: string, platform: string): Promise<SupadataVideoMetadataResponse | null> => {
  try {
    console.log(`Fetching video metadata from Supadata for ${platform}:`, videoId);
    
    const endpoint = platform === 'youtube' 
      ? `${SUPADATA_BASE_URL}/youtube/video?id=${videoId}`
      : `${SUPADATA_BASE_URL}/${platform}/video?id=${videoId}`;
    
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'x-api-key': SUPADATA_API_KEY,
        'Content-Type': 'application/json',
      }
    });
    
    console.log('Supadata metadata response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Supadata metadata API error:', response.status, errorText);
      throw new Error(`Supadata metadata API error: ${response.status} ${response.statusText}`);
    }
    
    const data: SupadataVideoMetadataResponse = await response.json();
    console.log('Supadata metadata received:', {
      title: data.title,
      transcriptLanguages: data.transcriptLanguages
    });
    
    return data;
  } catch (error) {
    console.error('Supadata metadata error:', error);
    return null;
  }
};

/**
 * Poll job status for async transcription processing
 */
const pollJobStatus = async (jobId: string, maxAttempts: number = 30): Promise<SupadataTranscriptResponse> => {
  console.log('Polling job status for:', jobId);
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await fetch(`${SUPADATA_BASE_URL}/transcript/job/${jobId}`, {
        method: 'GET',
        headers: {
          'x-api-key': SUPADATA_API_KEY,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`Job status API error: ${response.status} ${response.statusText}`);
      }
      
      const jobResult: SupadataTranscriptJobResponse = await response.json();
      console.log(`Job status attempt ${attempt}:`, jobResult.status);
      
      if (jobResult.status === 'completed') {
        console.log('Job completed successfully');
        return {
          content: (jobResult.content || '').trim(),
          lang: jobResult.lang || '',
          availableLangs: jobResult.availableLangs || [],
        };
      } else if (jobResult.status === 'failed') {
        throw new Error(`Transcription job failed: ${jobResult.error || 'Unknown error'}`);
      }
      
      // Job is still processing, wait before next poll
      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
      }
    } catch (error) {
      console.error(`Job polling error (attempt ${attempt}):`, error);
      if (attempt === maxAttempts) {
        throw error;
      }
    }
  }
  
  throw new Error('Transcription job timed out');
};

const requestSupadataTranscript = async (endpoint: string): Promise<SupadataTranscriptResponse> => {
  console.log(`Making API call to: ${endpoint}`);

  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      'x-api-key': SUPADATA_API_KEY,
      'Content-Type': 'application/json',
    }
  });

  console.log('Supadata transcript response status:', response.status);

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Supadata transcript API error:', response.status, errorText);
    throw new Error(`Supadata transcript API error: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();

  // Check if we got an immediate result (200) or a job ID for async processing (202)
  if (response.status === 202 && result.jobId) {
    console.log('Got job ID for async processing:', result.jobId);
    return await pollJobStatus(result.jobId);
  }

  return result as SupadataTranscriptResponse;
};

/**
 * Transcribe video using Supadata unified API for all platforms
 */
const transcribeWithSupadata = async (originalUrl: string, platform: string, preferredLang?: string): Promise<string> => {
  console.log(`Starting Supadata transcription for ${platform} URL:`, originalUrl);

  const encodedUrl = encodeURIComponent(originalUrl);
  const normalizedPreferredLang = normalizeLanguageCode(preferredLang);

  const attempts: Array<{ mode: 'native' | 'auto' | 'generate'; lang?: string; reason: string }> = normalizedPreferredLang
    ? [
        { mode: 'native', lang: normalizedPreferredLang, reason: 'Native transcript in detected original language' },
        { mode: 'generate', reason: 'Generated transcript in original audio language' },
      ]
    : [
        { mode: 'auto', reason: 'Auto transcript without language hint' },
        { mode: 'generate', reason: 'Generated transcript fallback in original audio language' },
      ];

  let lastError: unknown = null;

  for (const attempt of attempts) {
    try {
      let endpoint = `${SUPADATA_BASE_URL}/transcript?url=${encodedUrl}&text=true&mode=${attempt.mode}`;
      if (attempt.lang) {
        endpoint += `&lang=${encodeURIComponent(attempt.lang)}`;
      }

      console.log(`Trying Supadata transcript (${attempt.reason})`, {
        mode: attempt.mode,
        lang: attempt.lang,
      });

      const transcriptResult = await requestSupadataTranscript(endpoint);
      const transcriptContent = transcriptResult.content?.trim();
      const detectedTranscriptLang = normalizeLanguageCode(transcriptResult.lang);

      console.log('Supadata transcript result:', {
        contentLength: transcriptContent?.length || 0,
        lang: transcriptResult.lang,
        availableLangs: transcriptResult.availableLangs,
        preferredLang: normalizedPreferredLang,
      });

      if (!transcriptContent) {
        throw new Error('Empty transcription received');
      }

      if (normalizedPreferredLang && attempt.mode === 'native') {
        if (!detectedTranscriptLang || detectedTranscriptLang !== normalizedPreferredLang) {
          console.warn('Language mismatch on native transcript attempt, falling back to generate mode.', {
            expected: normalizedPreferredLang,
            got: detectedTranscriptLang || 'unknown',
          });
          continue;
        }
      }

      console.log('Supadata transcription successful, length:', transcriptContent.length);
      return transcriptContent;
    } catch (error) {
      lastError = error;
      console.error(`Supadata transcription attempt failed (${attempt.reason}):`, error);
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Unable to transcribe video with Supadata');
};

/**
 * Convert ISO 8601 duration to seconds
 */
const parseISO8601Duration = (duration: string): number => {
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return 0;
  
  const hours = parseInt(match[1] || '0');
  const minutes = parseInt(match[2] || '0');
  const seconds = parseInt(match[3] || '0');
  
  return hours * 3600 + minutes * 60 + seconds;
};

/**
 * Get video details using YouTube Data API v3 (fallback)
 */
export const getVideoDetails = async (videoId: string): Promise<VideoDetails | null> => {
  try {
    console.log('Fetching video details from YouTube API for:', videoId);
    
    const videoUrl = `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${videoId}&key=${YOUTUBE_API_KEY}`;
    
    const response = await fetch(videoUrl);
    console.log('YouTube API response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('YouTube API error:', response.status, errorText);
      throw new Error(`YouTube API error: ${response.status} ${response.statusText}`);
    }
    
    const data: YouTubeVideoResponse = await response.json();
    
    if (!data.items || data.items.length === 0) {
      throw new Error('Video not found');
    }
    
    const video = data.items[0];
    const durationInSeconds = parseISO8601Duration(video.contentDetails.duration);
    
    return {
      videoId,
      title: video.snippet.title,
      lengthSeconds: String(durationInSeconds),
      caption: false,
      description: video.snippet.description,
      defaultLanguage: video.snippet.defaultLanguage,
      defaultAudioLanguage: video.snippet.defaultAudioLanguage,
    };
  } catch (error) {
    console.error('Error fetching YouTube video data:', error);
    return null;
  }
};

/**
 * Generate transcription using video description and title when other methods fail
 */
const generateDescriptiveTranscription = (title: string, description?: string): string => {
  let transcription = `Video Title: ${title}\n\n`;
  
  if (description) {
    transcription += `Video Description:\n${description.substring(0, 1000)}`;
    if (description.length > 1000) {
      transcription += "... (description truncated)";
    }
  } else {
    transcription += "No description available for this video.";
  }
  
  transcription += `\n\nNote: This content is extracted from the video's metadata as audio transcription was not available.`;
  
  return transcription;
};

/**
 * Main function to get transcription from video URLs (YouTube, TikTok, Instagram, X)
 */
export const getVideoTranscription = async (url: string): Promise<{
  transcription: string;
  title: string;
  success: boolean;
  error?: string;
}> => {
  try {
    console.log('Starting video transcription for URL:', url);
    
    // Extract video ID and platform
    const videoInfo = extractVideoId(url);
    if (!videoInfo) {
      return {
        transcription: "",
        title: "",
        success: false,
        error: "Invalid video URL. Please provide a valid YouTube, TikTok, or Instagram URL."
      };
    }
    
    const { videoId, platform } = videoInfo;
    console.log(`Extracted ${platform} video ID:`, videoId);
    
    // Try Supadata transcription first
    try {
      console.log(`Attempting Supadata transcription for ${platform}...`);

      // Get video metadata from Supadata
      const metadata = await getSupadataVideoMetadata(videoId, platform);

      // Prefer language detection from YouTube API for YouTube videos,
      // then fall back to Supadata metadata transcript languages.
      let originalLang = normalizeLanguageCode(metadata?.transcriptLanguages?.[0]);
      let fallbackTitle = metadata?.title || "Unknown Video";

      if (platform === 'youtube') {
        const youtubeDetails = await getVideoDetails(videoId);
        const youtubeOriginalLang = normalizeLanguageCode(
          youtubeDetails?.defaultAudioLanguage || youtubeDetails?.defaultLanguage
        );

        if (youtubeOriginalLang) {
          originalLang = youtubeOriginalLang;
        }

        if (youtubeDetails?.title) {
          fallbackTitle = youtubeDetails.title;
        }
      }

      console.log(`Detected original language: ${originalLang || 'unknown (using source-audio fallback)'}`);

      // Always run through the improved language-preserving flow.
      // If native transcript language mismatches, it falls back to generate mode.
      const supadataTranscription = await transcribeWithSupadata(url, platform, originalLang);

      if (supadataTranscription && supadataTranscription.length > 0) {
        return {
          transcription: supadataTranscription,
          title: metadata?.title || fallbackTitle,
          success: true
        };
      }
    } catch (supadataError) {
      console.log('Supadata transcription failed:', supadataError);
    }
    
    // Fallback for YouTube only: Get video details from YouTube API and use metadata
    if (platform === 'youtube') {
      console.log('Falling back to YouTube API metadata...');
      const videoDetails = await getVideoDetails(videoId);
      
      if (videoDetails) {
        const descriptiveTranscription = generateDescriptiveTranscription(
          videoDetails.title, 
          videoDetails.description
        );
        
        return {
          transcription: descriptiveTranscription,
          title: videoDetails.title || "",
          success: true
        };
      }
    }
    
    // Final fallback
    return {
      transcription: "",
      title: "",
      success: false,
      error: `Failed to process ${platform} video with all available methods`
    };
    
  } catch (error) {
    console.error("Video transcription error:", error);
    return {
      transcription: "",
      title: "",
      success: false,
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
};

/**
 * Legacy function for backward compatibility
 */
export const getYouTubeTranscription = getVideoTranscription;

/**
 * Estimate processing time based on video duration
 */
export const estimateProcessingTime = (durationSeconds: number): string => {
  if (durationSeconds < 60) {
    return "a few seconds";
  } else if (durationSeconds < 300) { // 5 minutes
    return "about 15 seconds";
  } else if (durationSeconds < 900) { // 15 minutes
    return "30 seconds to 1 minute";
  } else if (durationSeconds < 3600) { // 1 hour
    return "1 to 2 minutes";
  } else {
    return "2 to 3 minutes";
  }
};
